<template>
    <PageWrapper contained>
        <div class="KbArticleView">
            <div class="KbArticleView__header">
                <KbBreadcrumbs :sections="sectionPath" :current="article ? article.title : ''" />
                <div v-if="article" class="KbArticleView__titleRow">
                    <h1 class="KbArticleView__title">
                        {{ article.title }}
                    </h1>
                    <NewButton
                        class="KbArticleView__copyLink"
                        size="sm"
                        color="secondary"
                        aria-label="Copy link to this article"
                        @click="copyLink"
                    >
                        Copy link
                    </NewButton>
                </div>
            </div>
            <div ref="body" class="KbArticleView__body">
                <div ref="articleColumn" class="KbArticleView__article">
                    <div v-if="isLoading" class="KbArticleView__loading">
                        Loading…
                    </div>
                    <div v-else-if="hasError" class="KbArticleView__error">
                        <p>This article is not available right now.</p>
                        <NewButton size="sm" color="primary" @click="reload">
                            Try again
                        </NewButton>
                    </div>
                    <KbArticleContent
                        v-else-if="article"
                        :article="article"
                        frame="page"
                        @navigate="navigate"
                    />
                </div>
                <aside
                    v-if="relatedArticles.length || (article && ownSection)"
                    class="KbArticleView__rail"
                    aria-label="More help"
                >
                    <KbArticleList
                        v-if="relatedArticles.length"
                        heading="Related articles"
                        heading-id="KbArticleList__heading-related"
                        :articles="relatedArticles"
                    />
                    <KbArticleList
                        v-if="article && ownSection"
                        heading="Articles in this section"
                        heading-id="KbArticleList__heading-section"
                        :articles="sectionArticles"
                        :current-article-id="article.id"
                    />
                </aside>
            </div>
        </div>
    </PageWrapper>
</template>

<style lang="scss">
.KbArticleView {
    display: grid;
    /* The header row keeps its height; the body row takes the rest and is what scrolls. */
    grid-template-rows: auto minmax(0, 1fr);
    gap: 16px;
    height: 100%;
    min-height: 0;

    &__header {
        display: flex;
        flex-direction: column;
        gap: 12px;
        /* Aligned with the article column so the title and body share a left edge. */
        width: 100%;
        max-width: 1080px;
        margin: 0 auto;
    }

    &__titleRow {
        display: flex;
        align-items: flex-start;
        justify-content: space-between;
        gap: 16px;
    }

    &__copyLink {
        flex-shrink: 0;
    }

    &__title {
        margin: 0;
        font-size: 24px;
        font-weight: 700;
        line-height: 1.3;
        color: var(--eidSecureBlue);
    }

    &__body {
        display: grid;
        grid-template-columns: minmax(0, 760px) 280px;
        gap: 40px;
        justify-content: center;
        min-height: 0;
    }

    &__article,
    &__rail {
        min-height: 0;
        overflow-y: auto;
    }

    &__rail {
        display: flex;
        flex-direction: column;
        gap: 24px;
        align-self: start;
        max-height: 100%;
    }

    /* One column: the header stays frozen and the body becomes the single scroll
       container, holding the article and then the rail. */
    @media (max-width: 1099px) {
        &__body {
            grid-template-columns: minmax(0, 1fr);
            grid-auto-rows: max-content;
            gap: 24px;
            overflow-y: auto;
        }

        &__article,
        &__rail {
            overflow-y: visible;
        }

        &__rail {
            max-height: none;
        }
    }
}
</style>

<script lang="ts">
    import { defineComponent } from 'vue';

    import { useSnackbarStore } from '@evidentid/dashboard-commons/modules/snackbar/store';
    import { KbArticle, KbArticleStub, KbSection } from '@evidentid/tprm-portal-lib/models/kb';
    import { OperationStatus } from '@evidentid/vue-commons/store/OperationStatus';

    import NewButton from '@/components/new-button/NewButton.vue';
    import PageWrapper from '@/components/page-wrapper/PageWrapper.vue';
    import { currentRpId, kbLocation, KbNavigationTarget, routeForTarget } from '@/modules/kb/components/KbArticleContent/helpers';
    import KbArticleContent from '@/modules/kb/components/KbArticleContent/KbArticleContent.vue';
    import KbArticleList from '@/modules/kb/components/KbArticleList/KbArticleList.vue';
    import KbBreadcrumbs from '@/modules/kb/components/KbBreadcrumbs/KbBreadcrumbs.vue';
    import { useKbStore } from '@/modules/kb/store';
    import { findRelatedArticles } from '@/modules/kb/utils/relatedArticles';
    import { findSectionPath } from '@/modules/kb/utils/sectionPath';

    export default defineComponent({
        name: 'KbArticleView',
        components: { KbArticleContent, KbArticleList, KbBreadcrumbs, NewButton, PageWrapper },
        computed: {
            kbStore() {
                return useKbStore();
            },
            articleId(): string {
                return String(this.$route.params.articleId);
            },
            article(): KbArticle | null {
                return this.kbStore.articleFor(this.articleId) ?? null;
            },
            sectionPath(): KbSection[] {
                return this.article ? findSectionPath(this.kbStore.tree, this.article.sectionId) : [];
            },
            ownSection(): KbSection | null {
                return this.sectionPath[this.sectionPath.length - 1] ?? null;
            },
            relatedArticles(): KbArticleStub[] {
                return findRelatedArticles(this.kbStore.tree, this.articleId);
            },
            sectionArticles(): KbArticleStub[] {
                return [ ...(this.ownSection?.articles ?? []) ].sort((a, b) => a.position - b.position);
            },
            isLoading(): boolean {
                const status = this.kbStore.articleStatusFor(this.articleId);
                return status === undefined || status === OperationStatus.loading;
            },
            hasError(): boolean {
                return this.kbStore.articleStatusFor(this.articleId) === OperationStatus.error;
            },
        },
        watch: {
            articleId: {
                immediate: true,
                handler(id: string): void {
                    this.kbStore.loadArticle(id);
                    this.scrollToTop();
                },
            },
        },
        created(): void {
            this.kbStore.loadTree();
        },
        methods: {
            // The route view is reused between articles, so the previous scroll offset would
            // carry over: the article column scrolls on wide screens, the body on narrow ones.
            scrollToTop(): void {
                this.$nextTick(() => {
                    [ this.$refs.articleColumn, this.$refs.body ].forEach((element) => {
                        if (element instanceof HTMLElement) {
                            element.scrollTop = 0;
                        }
                    });
                });
            },
            reload(): void {
                this.kbStore.loadArticle(this.articleId, { force: true });
            },
            // The tenant-free URL, so whoever opens it lands in their own client.
            async copyLink(): Promise<void> {
                const { href } = this.$router.resolve(kbLocation({ kind: 'article', id: this.articleId }));
                const url = `${window.location.origin}${window.location.pathname}${href}`;
                try {
                    if (!navigator.clipboard) {
                        throw new Error('Clipboard unavailable');
                    }
                    await navigator.clipboard.writeText(url);
                    await useSnackbarStore().displaySnackbar({
                        message: 'Link copied — works for any customer',
                        success: true,
                    });
                } catch {
                    await useSnackbarStore().displaySnackbar({ message: 'Couldn\'t copy the link', success: false });
                }
            },
            navigate(target: KbNavigationTarget): void {
                this.$router.push(routeForTarget(target, currentRpId(this.$route)));
            },
        },
    });
</script>
